import { create } from 'zustand';
import type { Lang } from '@/lib/i18n';

export type AppView = 'owner' | 'accountant' | 'burs';

/**
 * Display modes — control the global theme (CSS variables on <html>).
 *
 *   - normal     → original brand tokens (cream paper, ink text, amber accent)
 *   - operatory  → dimmed dark theme for clinical rooms (audit Part 4 §4)
 *   - fun        → playful 3D-ish dark theme, easter-egg only (audit Part 4 §5)
 *
 * The DOM attribute is set in main.tsx on init and re-set on every change.
 * Persisting in localStorage means refreshing keeps the chosen mode.
 */
export type AppMode = 'normal' | 'operatory' | 'fun';

interface UiState {
  drawerOpen: boolean;
  view: AppView;
  lang: Lang;
  mode: AppMode;
  setDrawer: (open: boolean) => void;
  setView: (view: AppView) => void;
  setLang: (lang: Lang) => void;
  setMode: (mode: AppMode) => void;
}

const STORED_LANG = ((): Lang => {
  try {
    const raw = localStorage.getItem('np.lang');
    const valid: Lang[] = ['en', 'tn', 'zu', 'ha', 'pcm', 'sn'];
    return (valid as string[]).includes(raw ?? '') ? (raw as Lang) : 'en';
  } catch {
    return 'en';
  }
})();

const STORED_MODE = ((): AppMode => {
  try {
    const raw = localStorage.getItem('np.mode');
    if (raw === 'operatory' || raw === 'fun') return raw;
    return 'normal';
  } catch {
    return 'normal';
  }
})();

function applyModeAttribute(mode: AppMode) {
  if (typeof document === 'undefined') return;
  if (mode === 'normal') {
    document.documentElement.removeAttribute('data-np-mode');
  } else {
    document.documentElement.setAttribute('data-np-mode', mode);
  }
}

// Apply once on module load so the very first render uses the correct theme.
applyModeAttribute(STORED_MODE);

export const useUiStore = create<UiState>((set) => ({
  drawerOpen: false,
  view: (localStorage.getItem('np.view') as AppView) ?? 'owner',
  lang: STORED_LANG,
  mode: STORED_MODE,

  setDrawer: (drawerOpen) => set({ drawerOpen }),

  setView: (view) => {
    localStorage.setItem('np.view', view);
    set({ view });
  },

  setLang: (lang) => {
    localStorage.setItem('np.lang', lang);
    set({ lang });
  },

  setMode: (mode) => {
    try {
      localStorage.setItem('np.mode', mode);
    } catch {
      // private mode — ignore
    }
    applyModeAttribute(mode);
    set({ mode });
  },
}));
